import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import ForceGraph3D, { ForceGraphMethods } from 'react-force-graph-3d';
import * as THREE from 'three';
import { GraphData, GraphNode, GraphEdge } from './types';
import { useGraph3DData, Graph3DNode, Graph3DCluster } from './graph3d/useGraph3DData';
import { createNodeObject } from './graph3d/nodeObject';
import { AUTO_ROTATE_SPEED, FLY_TO_DURATION_MS, FLY_TO_DISTANCE, LABEL_VISIBLE_DISTANCE } from './graph3d/constants';

interface Canvas3DProps {
  data?: GraphData;
  onNodeSelect?: (node: GraphNode) => void;
  onEdgeSelect?: (edge: GraphEdge) => void;
  searchQuery?: string;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  node: Graph3DNode | null;
}

export default function Canvas3D({ data, onNodeSelect, onEdgeSelect, searchQuery }: Canvas3DProps) {
  const fgRef = useRef<ForceGraphMethods | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoRotateKilledRef = useRef(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hiddenClusters, setHiddenClusters] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, node: null });

  const graph3DData = useGraph3DData(data);

  // Filtered graph data respecting hidden clusters and search
  const filteredGraphData = useMemo(() => {
    const matchSearch = (node: Graph3DNode) => {
      if (!searchQuery) return true;
      return node.label.toLowerCase().includes(searchQuery.toLowerCase());
    };

    const visibleNodes = graph3DData.nodes.filter(
      n => !hiddenClusters.has(n.clusterId) && matchSearch(n)
    );
    const visibleIds = new Set(visibleNodes.map(n => n.id));
    const visibleLinks = graph3DData.links.filter(
      l => visibleIds.has(l.source as string) && visibleIds.has(l.target as string)
    );
    return { nodes: visibleNodes, links: visibleLinks };
  }, [graph3DData, hiddenClusters, searchQuery]);

  // Connected node IDs for selected node highlight
  const connectedIds = useMemo(() => {
    if (!selectedNodeId) return new Set<string>();
    const ids = new Set<string>([selectedNodeId]);
    graph3DData.links.forEach(l => {
      const src = typeof l.source === 'object' ? (l.source as any).id : l.source;
      const tgt = typeof l.target === 'object' ? (l.target as any).id : l.target;
      if (src === selectedNodeId) ids.add(tgt);
      if (tgt === selectedNodeId) ids.add(src);
    });
    return ids;
  }, [selectedNodeId, graph3DData.links]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Set up auto-orbit on mount
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const controls = fg.controls() as any;
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = AUTO_ROTATE_SPEED;
    }
    autoRotateKilledRef.current = false;
  }, [fgRef.current]);

  const killAutoRotate = useCallback(() => {
    if (autoRotateKilledRef.current) return;
    autoRotateKilledRef.current = true;
    const fg = fgRef.current;
    if (!fg) return;
    const controls = fg.controls() as any;
    if (controls) controls.autoRotate = false;
  }, []);

  const handleNodeClick = useCallback((node: Graph3DNode) => {
    killAutoRotate();
    setSelectedNodeId(node.id);

    const graphNode: GraphNode = {
      id: node.id,
      kind: node.kind,
      label: node.label,
      status: node.status as any,
      tags: [],
      props: node.props || {},
    };
    onNodeSelect?.(graphNode);

    // Fly camera to node
    const fg = fgRef.current;
    if (fg && node.x !== undefined) {
      const dist = FLY_TO_DISTANCE;
      const distRatio = 1 + dist / Math.hypot(node.x ?? 0, node.y ?? 0, node.z ?? 0);
      fg.cameraPosition(
        { x: (node.x ?? 0) * distRatio, y: (node.y ?? 0) * distRatio, z: (node.z ?? 0) * distRatio },
        { x: node.x ?? 0, y: node.y ?? 0, z: node.z ?? 0 },
        FLY_TO_DURATION_MS
      );
    }
  }, [onNodeSelect, killAutoRotate]);

  const handleNodeHover = useCallback((node: Graph3DNode | null, prevNode: Graph3DNode | null) => {
    if (node) {
      setTooltip({ visible: true, x: 0, y: 0, node });
    } else {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setTooltip(prev => ({ ...prev, x: e.clientX + 14, y: e.clientY - 10 }));
  }, []);

  const handleBackgroundClick = useCallback(() => {
    killAutoRotate();
    setSelectedNodeId(null);
  }, [killAutoRotate]);

  const toggleCluster = useCallback((clusterId: string) => {
    setHiddenClusters(prev => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  }, []);

  const nodeThreeObject = useCallback((node: Graph3DNode) => {
    const isSelected = selectedNodeId === node.id;
    const isConnected = connectedIds.has(node.id);
    const isHighlighted = isSelected || (selectedNodeId !== null && isConnected);
    return createNodeObject(node, isHighlighted);
  }, [selectedNodeId, connectedIds]);

  const linkColor = useCallback((link: any) => {
    if (!selectedNodeId) return link.color || '#475569';
    const src = typeof link.source === 'object' ? link.source.id : link.source;
    const tgt = typeof link.target === 'object' ? link.target.id : link.target;
    if (src === selectedNodeId || tgt === selectedNodeId) return '#F59E0B';
    return '#1E293B';
  }, [selectedNodeId]);

  const linkWidth = useCallback((link: any) => {
    if (!selectedNodeId) return link.width ?? 1;
    const src = typeof link.source === 'object' ? link.source.id : link.source;
    const tgt = typeof link.target === 'object' ? link.target.id : link.target;
    return (src === selectedNodeId || tgt === selectedNodeId) ? 3 : 0.5;
  }, [selectedNodeId]);

  const linkOpacity = useCallback((link: any) => {
    if (!selectedNodeId) return link.opacity ?? 0.5;
    const src = typeof link.source === 'object' ? link.source.id : link.source;
    const tgt = typeof link.target === 'object' ? link.target.id : link.target;
    return (src === selectedNodeId || tgt === selectedNodeId) ? 1 : 0.1;
  }, [selectedNodeId]);

  if (!data || data.nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="text-lg font-medium">No data to display</div>
          <div className="text-sm mt-1">Select an IKU category or wait for data to load</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full w-full relative bg-gray-950" onMouseMove={handleMouseMove}>
      <ForceGraph3D
        ref={fgRef}
        graphData={filteredGraphData as any}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="#030712"
        nodeThreeObject={nodeThreeObject as any}
        nodeThreeObjectExtend={false}
        nodeLabel=""
        linkColor={linkColor as any}
        linkWidth={linkWidth as any}
        linkOpacity={linkOpacity as any}
        linkDirectionalParticles={(link: any) => link.animated ? 3 : 0}
        linkDirectionalParticleWidth={1.5}
        linkDirectionalParticleColor={(link: any) => link.color || '#F59E0B'}
        onNodeClick={handleNodeClick as any}
        onNodeHover={handleNodeHover as any}
        onBackgroundClick={handleBackgroundClick}
        enableNodeDrag={true}
        onNodeDragEnd={killAutoRotate}
        cooldownTicks={120}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
      />

      {/* Tooltip */}
      {tooltip.visible && tooltip.node && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 pointer-events-none border border-gray-700 max-w-xs"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className="font-semibold text-sm mb-1" style={{ color: tooltip.node.color }}>
            {tooltip.node.label}
          </div>
          <div className="text-gray-400 mb-1">{tooltip.node.kind}</div>
          {tooltip.node.status && tooltip.node.status !== 'unknown' && (
            <div className={`text-xs ${tooltip.node.status === 'ok' ? 'text-green-400' : tooltip.node.status === 'warn' ? 'text-yellow-400' : 'text-red-400'}`}>
              Status: {tooltip.node.status}
            </div>
          )}
        </div>
      )}

      {/* Cluster Legend */}
      <div className="absolute top-4 right-4 bg-gray-900/90 backdrop-blur rounded-lg shadow-xl border border-gray-700 p-3 min-w-[180px]">
        <div className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Clusters</div>
        <div className="space-y-1.5">
          {graph3DData.clusters.map(cluster => {
            const isHidden = hiddenClusters.has(cluster.id);
            return (
              <button
                key={cluster.id}
                onClick={() => toggleCluster(cluster.id)}
                className={`w-full flex items-center gap-2 text-left text-xs px-1.5 py-1 rounded transition-all ${
                  isHidden ? 'opacity-40' : 'opacity-100'
                } hover:bg-gray-700/50`}
              >
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: cluster.color }}
                />
                <span className="flex-1 text-gray-300 truncate">{cluster.label}</span>
                <span className="text-gray-500">{cluster.nodeCount}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-700 text-[10px] text-gray-500">
          {filteredGraphData.nodes.length} nodes · {filteredGraphData.links.length} edges
        </div>
      </div>

      {/* Selection indicator */}
      {selectedNodeId && (
        <div className="absolute bottom-4 left-4 bg-gray-900/90 text-white text-xs px-3 py-1.5 rounded-lg border border-gray-700">
          <span className="text-gray-400">Selected: </span>
          <span className="font-medium">
            {graph3DData.nodes.find(n => n.id === selectedNodeId)?.label || selectedNodeId}
          </span>
          <button
            onClick={() => setSelectedNodeId(null)}
            className="ml-2 text-gray-500 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
